import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import ReportsMenuStructure from "../../menuStructures/ReportsMenuStructure";
import SetActiveMenu from "../../menuStructures/SetActiveMenu";
import {
  useDataExportHistory,
  useDataExportJobPoller,
  useDataExportRegistry,
  useTriggerDataExport,
  useCancelDataExportJob,
} from "../../hooks/dataExportHooks";
import type {
  DataExportHistoryItem,
  DataExportJobStatus,
  DataExportRegistryItem,
} from "../../types/dataExport";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  queued: "🕒 Queued",
  processing: "⏳ Processing",
  ready: "✅ Ready",
  streamed: "⬇ Streamed",
  error: "❌ Failed",
  cancelled: "🚫 Cancelled",
};

/** Set true to show batch size input for cascade exports that support it. */
const SHOW_BATCH_SIZE_CONTROL = false;

/** Fallbacks when registry omits batch size bounds (matches API defaults). */
const MIN_BATCH_SIZE = 500;
const MAX_BATCH_SIZE = 5000;
const DEFAULT_BATCH_SIZE = 1000;

const STATUS_CLASS: Record<string, string> = {
  queued: "",
  processing: "",
  ready: "",
  streamed: "",
  error: "",
  cancelled: "",
};

/**
 * Downloads a remote file with a specific filename.
 * Strategy 1: fetch as blob → create object URL → anchor download (gives full
 *   control over filename; works when GCS bucket has CORS configured).
 * Strategy 2 (fallback): direct anchor click with download attribute — some
 *   browsers honour this even for cross-origin pre-signed URLs.
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  // Strategy 1 — blob fetch (controls filename reliably)
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    return;
  } catch {
    // CORS or network error — fall through to strategy 2
  }

  // Strategy 2 — anchor with download attribute
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function formatDate(isoString: string | null): string {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

type DownloadCellProps = {
  item: DataExportHistoryItem;
};

function DownloadCell({ item }: DownloadCellProps) {
  const cell: React.CSSProperties = { fontSize: "0.8em" };
  const link: React.CSSProperties = {
    ...cell,
    background: "none",
    border: "none",
    padding: 0,
    color: "#007bff",
    cursor: "pointer",
    textDecoration: "underline",
    fontFamily: "inherit",
  };

  if (item.status === "error") {
    return (
      <span style={{ ...cell, color: "#dc3545" }}>
        {item.errorMessage ?? "Error"}
      </span>
    );
  }
  if (item.status === "streamed") {
    return (
      <span style={{ ...cell, color: "#6c757d" }}>Downloaded directly</span>
    );
  }
  if (item.status === "processing") {
    return <span style={{ ...cell, color: "#6c757d" }}>&mdash;</span>;
  }
  if (item.status === "ready" && !item.downloadUrls?.length) {
    return (
      <span style={{ ...cell, color: "#6c757d" }}>
        No data found for this export
      </span>
    );
  }
  if (!item.downloadUrls?.length) {
    return <span style={{ ...cell, color: "#6c757d" }}>&mdash;</span>;
  }

  // Derive consistent names from export label + completed date
  const count = item.downloadUrls.length;
  const safeName = item.label.replace(/[/\\:*?"<>|]/g, "").trim();
  const completedDate = item.updatedAt
    ? (() => {
        const d = new Date(item.updatedAt as string);
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )} ${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
      })()
    : "";
  const dateSuffix = completedDate ? ` ${completedDate}` : "";

  // Derive the real file extension from the URL (strip query params first).
  // Handles both .csv (individual exports) and .zip (encrypted / "Download All").
  // Falls back to .csv if the URL has no recognisable extension.
  const urlExt = (url: string): string => {
    try {
      const path = url.split("?")[0];
      const lastSegment = path.split("/").pop() ?? "";
      const dot = lastSegment.lastIndexOf(".");
      if (dot > 0) return lastSegment.slice(dot); // e.g. ".zip" or ".csv"
    } catch {
      // ignore
    }
    return ".csv";
  };

  // Display name (UI link text): no extension — clean and format-agnostic
  const displayName = (i: number) =>
    count > 1 ? `${safeName} Part${i + 1}` : safeName;

  // Download filename: includes timestamp + correct extension so the OS opens
  // the file with the right application
  const downloadName = (i: number) =>
    count > 1
      ? `${safeName} Part${i + 1}${dateSuffix}${urlExt(item.downloadUrls[i])}`
      : `${safeName}${dateSuffix}${urlExt(item.downloadUrls[i])}`;

  return (
    <div>
      {count > 1 && (
        <div style={{ ...cell, color: "#6c757d", marginBottom: 2 }}>
          {count} files
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {item.downloadUrls.map((url, i) => (
          <li key={url} style={{ marginBottom: 1 }}>
            <button
              style={link}
              onClick={() => downloadFile(url, downloadName(i))}
            >
              &#x2193; {displayName(i)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

function DataExports() {
  useEffect(() => {
    document.title = "Data Exports | BinderPOS";
  }, []);

  // ── Registry + history ────────────────────────────────────────────────────
  const { data: registry = [], isLoading: registryLoading } =
    useDataExportRegistry();
  const {
    data: history = [],
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useDataExportHistory();

  // ── Form state ────────────────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string>("");
  const [deliveryMode, setDeliveryMode] = useState<"sync" | "async">("sync");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [batchSize, setBatchSize] = useState("");

  // Active jobs being polled (jobId → label)
  const [activePolls, setActivePolls] = useState<Map<number, string>>(
    new Map()
  );

  const selectedDescriptor: DataExportRegistryItem | undefined = useMemo(
    () => registry.find((r) => r.id === selectedId),
    [registry, selectedId]
  );

  const batchSizeLimits = useMemo(() => {
    if (!SHOW_BATCH_SIZE_CONTROL || !selectedDescriptor?.supportsBatchSize) {
      return null;
    }
    return {
      min: selectedDescriptor.batchSizeMin ?? MIN_BATCH_SIZE,
      max: selectedDescriptor.batchSizeMax ?? MAX_BATCH_SIZE,
      default: selectedDescriptor.batchSizeDefault ?? DEFAULT_BATCH_SIZE,
    };
  }, [selectedDescriptor]);

  // When user picks a different export, reset delivery to default and clear date fields
  useEffect(() => {
    if (!selectedDescriptor) return;
    if (selectedDescriptor.delivery === "async") setDeliveryMode("async");
    else setDeliveryMode("sync"); // sync or both → prefer Download Now
    setDateFrom("");
    setDateTo("");
    setBatchSize("");
  }, [selectedDescriptor]);

  // ── Polling ───────────────────────────────────────────────────────────────
  const onPollComplete = useCallback(
    (result: DataExportJobStatus, label: string) => {
      setActivePolls((prev) => {
        const next = new Map(prev);
        next.delete(result.jobId);
        return next;
      });
      if (result.status === "ready") {
        if (!result.downloadUrls?.length) {
          toast.info(`ℹ️ "${label}" export completed — no data found.`);
        } else {
          toast.success(`✅ "${label}" export is ready to download!`);
        }
      } else {
        toast.error(
          `❌ "${label}" export failed: ${
            result.errorMessage ?? "unknown error"
          }`
        );
      }
      refetchHistory();
    },
    [refetchHistory]
  );

  // We need one poller per active job — managed via a map of JobPollerRow components
  // (see JobPollerRow below)

  // ── Trigger export ────────────────────────────────────────────────────────
  const onAsyncQueued = useCallback(
    (jobId: number, label: string) => {
      setActivePolls((prev) => new Map(prev).set(jobId, label));
      toast.info(
        `📬 "${label}" export queued — you'll be notified when ready.`
      );
      refetchHistory();
    },
    [refetchHistory]
  );

  const {
    trigger,
    loading: triggering,
    error: triggerError,
    clearError,
  } = useTriggerDataExport(onAsyncQueued);

  const { cancel: cancelJob, cancellingIds } = useCancelDataExportJob();

  const handleCancel = useCallback(
    async (jobId: number) => {
      const ok = await cancelJob(jobId);
      if (ok) {
        setActivePolls((prev) => {
          const next = new Map(prev);
          next.delete(jobId);
          return next;
        });
      }
    },
    [cancelJob]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDescriptor) return;
    clearError();

    if (
      SHOW_BATCH_SIZE_CONTROL &&
      selectedDescriptor.supportsBatchSize &&
      batchSize.trim()
    ) {
      const parsed = Number(batchSize);
      const min = batchSizeLimits?.min ?? MIN_BATCH_SIZE;
      const max = batchSizeLimits?.max ?? MAX_BATCH_SIZE;
      if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
        toast.error(
          `Batch size must be a whole number between ${min.toLocaleString()} and ${max.toLocaleString()}.`
        );
        return;
      }
    }

    await trigger(
      {
        exportId: selectedId,
        mode: deliveryMode,
        ...(selectedDescriptor.supportsDateRange && dateFrom
          ? { dateFrom }
          : {}),
        ...(selectedDescriptor.supportsDateRange && dateTo ? { dateTo } : {}),
        ...(SHOW_BATCH_SIZE_CONTROL &&
        selectedDescriptor.supportsBatchSize &&
        batchSize.trim()
          ? { batchSize: Number(batchSize) }
          : {}),
      },
      selectedDescriptor.label
    );
  };

  // ── Group registry by category for dropdown ───────────────────────────────
  const grouped = useMemo(() => {
    const map = new Map<string, DataExportRegistryItem[]>();
    for (const item of registry) {
      const categoryItems = map.get(item.category);
      if (categoryItems) {
        categoryItems.push(item);
      } else {
        map.set(item.category, [item]);
      }
    }
    return map;
  }, [registry]);

  const categoryLabel = (cat: string) =>
    cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, " ");

  const isAsyncOnly = selectedDescriptor?.delivery === "async";
  const isBoth = selectedDescriptor?.delivery === "both";
  const supportsBatchSize =
    SHOW_BATCH_SIZE_CONTROL && !!selectedDescriptor?.supportsBatchSize;

  return (
    <Fragment>
      <SetActiveMenu menuStructure={ReportsMenuStructure} />

      <div className="app-header">
        <h1 className="title">
          <span className="header-text">Data Exports</span>
        </h1>
      </div>

      <div>
        {/* ── New Export panel ─────────────────────────────────────────── */}
        <div className="card mb-4">
          <div className="card-header fw-semibold">New Export</div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {/* Controls flow left-to-right, fixed widths, uniform gap */}
              <div
                className="d-flex flex-wrap align-items-end"
                style={{ gap: "20px" }}
              >
                {/* Export type */}
                <div style={{ width: 220 }}>
                  <label
                    className="form-label mb-1 small text-uppercase text-muted"
                    style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                  >
                    Export Type
                  </label>
                  {registryLoading ? (
                    <p className="text-muted small mb-0">Loading exports…</p>
                  ) : (
                    <select
                      className="form-control form-control-sm"
                      style={{ height: 36 }}
                      value={selectedId}
                      onChange={(e) => {
                        setSelectedId(e.target.value);
                        clearError();
                      }}
                      required
                    >
                      <option value="" disabled>
                        Select an export…
                      </option>
                      {Array.from(grouped.entries()).map(([cat, items]) => (
                        <optgroup key={cat} label={categoryLabel(cat)}>
                          {items.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  )}
                </div>

                {/* Delivery mode — only for 'both' exports */}
                {isBoth && (
                  <div style={{ width: 210 }}>
                    <label
                      className="form-label mb-1 small text-uppercase text-muted"
                      style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                    >
                      Delivery
                    </label>
                    <select
                      className="form-control form-control-sm"
                      style={{ height: 36 }}
                      value={deliveryMode}
                      onChange={(e) =>
                        setDeliveryMode(e.target.value as "sync" | "async")
                      }
                    >
                      <option value="sync">⚡ Download Now</option>
                      <option value="async">📬 Background</option>
                    </select>
                  </div>
                )}

                {/* Batch size — when enabled and supported by export type */}
                {supportsBatchSize && batchSizeLimits && (
                  <div style={{ width: 160 }}>
                    <label
                      className="form-label mb-1 small text-uppercase text-muted"
                      style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                    >
                      Batch Size ({batchSizeLimits.min.toLocaleString()} -{" "}
                      {batchSizeLimits.max.toLocaleString()})
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ height: 36 }}
                      value={batchSize}
                      min={batchSizeLimits.min}
                      max={batchSizeLimits.max}
                      step={1}
                      placeholder={String(batchSizeLimits.default)}
                      title={`Default ${batchSizeLimits.default.toLocaleString()}`}
                      onChange={(e) => setBatchSize(e.target.value)}
                    />
                  </div>
                )}

                {/* Date range — only when supported */}
                {selectedDescriptor?.supportsDateRange && (
                  <>
                    <div style={{ width: 150 }}>
                      <label
                        className="form-label mb-1 small text-uppercase text-muted"
                        style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                      >
                        From
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ height: 36 }}
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div style={{ width: 150 }}>
                      <label
                        className="form-label mb-1 small text-uppercase text-muted"
                        style={{ letterSpacing: "0.04em", fontWeight: 600 }}
                      >
                        To
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ height: 36 }}
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Export button — invisible label keeps it top-aligned with the other fields; extra left margin separates action from fields */}
                <div style={{ marginLeft: 12 }}>
                  <label
                    className="form-label mb-1 small"
                    style={{ visibility: "hidden" }}
                  >
                    &nbsp;
                  </label>
                  <div>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      style={{
                        height: 36,
                        paddingLeft: 20,
                        paddingRight: 20,
                        fontWeight: 600,
                      }}
                      disabled={!selectedId || triggering}
                    >
                      {triggering
                        ? deliveryMode === "sync"
                          ? "Downloading…"
                          : "Queuing…"
                        : "Start Export"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Async-only notice */}
              {isAsyncOnly && selectedDescriptor && (
                <div className="alert alert-info py-2 small mt-3 mb-0">
                  ⓘ <strong>{selectedDescriptor.label}</strong> runs in the
                  background. You&apos;ll receive an email when your file is
                  ready. Results appear in the history below.
                </div>
              )}

              {/* Error / in-progress warning */}
              {triggerError && (
                <div
                  className={`alert py-2 small mt-3 mb-0 ${
                    triggerError
                      .toLowerCase()
                      .includes("already in progress") ||
                    triggerError.toLowerCase().includes("already in queue")
                      ? "alert-warning"
                      : "alert-danger"
                  }`}
                >
                  {triggerError}
                </div>
              )}
            </form>
          </div>
        </div>

        {/* ── Export History ───────────────────────────────────────────── */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold">Export History</span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => refetchHistory()}
            >
              ↻ Refresh
            </button>
          </div>
          <div className="card-body p-0">
            {historyLoading ? (
              <p className="text-muted p-3 mb-0">Loading history…</p>
            ) : history.length === 0 ? (
              <p className="text-muted p-3 mb-0">No exports yet.</p>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-hover table-sm mb-0"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#007d7d",
                        color: "#fff",
                        fontWeight: 600,
                        borderLeft: "none",
                        lineHeight: "1.4",
                      }}
                    >
                      <th style={{ whiteSpace: "nowrap" }}>Job ID ↓</th>
                      <th style={{ whiteSpace: "nowrap" }}>Correlation ID</th>
                      <th>Export</th>
                      <th>Exported By</th>
                      <th>Started</th>
                      <th>Completed</th>
                      <th>Status</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr
                        key={item.id}
                        style={{ borderLeft: "none", lineHeight: "1.4" }}
                      >
                        <td
                          className="align-middle text-muted small font-monospace"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          #{item.id}
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px" }}
                        >
                          <span
                            className="font-monospace"
                            style={{
                              fontSize: "0.7rem",
                              color: "#6c757d",
                              letterSpacing: "-0.01em",
                              cursor: "pointer",
                              userSelect: "all",
                            }}
                            title="Click to select and copy for log search"
                          >
                            {item.correlationId ?? "—"}
                          </span>
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px" }}
                        >
                          {item.label}
                        </td>
                        <td
                          className="align-middle text-muted small"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          {item.exportedByEmail ?? "—"}
                        </td>
                        <td
                          className="align-middle text-muted small"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          {formatDate(item.createdAt)}
                        </td>
                        <td
                          className="align-middle text-muted small"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          {item.status === "processing" ||
                          item.status === "queued"
                            ? "—"
                            : formatDate(item.updatedAt)}
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px", whiteSpace: "nowrap" }}
                        >
                          <span
                            className={`badge ${
                              STATUS_CLASS[item.status] ?? ""
                            }`}
                            style={
                              item.status === "processing" ||
                              item.status === "queued"
                                ? {
                                    backgroundColor: "#f59e0b",
                                    color: "#1a1a1a",
                                    padding: "3px 8px",
                                    borderRadius: 4,
                                  }
                                : undefined
                            }
                          >
                            {STATUS_LABEL[item.status] ?? item.status}
                          </span>
                          {(item.status === "processing" ||
                            item.status === "queued") && (
                            <button
                              title="Cancel this job"
                              disabled={cancellingIds.has(item.id)}
                              onClick={() => handleCancel(item.id)}
                              style={{
                                marginLeft: 6,
                                background: "none",
                                border: "none",
                                padding: "0 2px",
                                cursor: "pointer",
                                color: "#dc3545",
                                fontSize: "0.9em",
                                lineHeight: 1,
                                verticalAlign: "middle",
                                opacity: cancellingIds.has(item.id) ? 0.4 : 1,
                              }}
                            >
                              {cancellingIds.has(item.id) ? "…" : "✕"}
                            </button>
                          )}
                        </td>
                        <td
                          className="align-middle"
                          style={{ padding: "8px 10px" }}
                        >
                          <DownloadCell item={item} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden pollers — one per active async job */}
      {Array.from(activePolls.entries()).map(([jobId, label]) => (
        <JobPollerRow
          key={jobId}
          jobId={jobId}
          label={label}
          onComplete={onPollComplete}
        />
      ))}
    </Fragment>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Invisible polling component — one instance per active job
// ─────────────────────────────────────────────────────────────────────────────

type JobPollerRowProps = {
  jobId: number;
  label: string;
  onComplete: (result: DataExportJobStatus, label: string) => void;
};

function JobPollerRow({ jobId, label, onComplete }: JobPollerRowProps) {
  const handleComplete = useCallback(
    (result: DataExportJobStatus) => onComplete(result, label),
    [onComplete, label]
  );
  const { startPolling, stopPolling } = useDataExportJobPoller(
    jobId,
    handleComplete
  );

  useEffect(() => {
    startPolling(jobId);
    return () => stopPolling();
  }, [jobId, startPolling, stopPolling]);

  return null;
}

export default DataExports;
